import type { ResolvedPos } from '@tiptap/pm/model'
import { findInstalledSlashCommand, TInstalledSlashCommand } from '@/atoms/slash-commands'
import type { TSlashCommandArg } from '@/lib/slash-command-definition'

/**
 * A slash-command invocation looks like:
 *   [/cmd@nostr:npub… slashCommand node][text: " arg1 arg2 …"]
 * with the node at the very start of the content (only whitespace before it).
 * Returns the node's attrs and where the args region starts, when the cursor
 * sits in the same textblock after it.
 */
export function findInvocation($pos: ResolvedPos): {
  pubkey: string
  d: string
  argsStart: number
} | null {
  const parent = $pos.parent
  if (!parent.isTextblock) return null

  const first = parent.firstChild
  if (!first || first.type.name !== 'slashCommand') return null
  if ($pos.doc.textBetween(0, $pos.before(), '\n', ' ').trim() !== '') return null

  const argsStart = $pos.start() + first.nodeSize
  if ($pos.pos < argsStart) return null

  return { pubkey: first.attrs.pubkey, d: first.attrs.d, argsStart }
}

export type TArgsContext = {
  slashCommand: TInstalledSlashCommand
  /** index of the arg being typed at the cursor */
  index: number
  arg: TSlashCommandArg
  /** the word under the cursor, which completions replace */
  range: { from: number; to: number }
  /** the part of that word before the cursor */
  query: string
}

/**
 * Work out which argument of which installed command the cursor is on. Pure:
 * everything comes from the doc and the installed commands.
 */
export function resolveArgsContext($pos: ResolvedPos): TArgsContext | null {
  const invocation = findInvocation($pos)
  if (!invocation) return null

  const slashCommand = findInstalledSlashCommand(invocation.pubkey, invocation.d)
  if (!slashCommand || slashCommand.args.length === 0) return null

  const before = $pos.doc.textBetween(invocation.argsStart, $pos.pos, '\n', ' ')
  // args must be separated from the command by whitespace
  if (!/^\s/.test(before)) return null

  const words = before.match(/\S+/g) ?? []
  const typingWord = !/\s$/.test(before)

  // the word being typed is the one at the cursor; it has to sit in a text
  // node so we can map it back to doc positions (not e.g. right after a mention)
  let query = ''
  if (typingWord) {
    const nodeBefore = $pos.nodeBefore
    if (!nodeBefore?.isText) return null
    query = nodeBefore.text!.match(/\S*$/)![0]
  }

  const index = typingWord ? words.length - 1 : words.length
  // a `text` arg consumes the remainder of the content, nothing to suggest after it
  const textIndex = slashCommand.args.findIndex((arg) => arg.type === 'text')
  if (textIndex !== -1 && index > textIndex) return null

  const arg = slashCommand.args[index]
  if (!arg) return null

  // extend the range to the end of the word so completions replace all of it
  const nodeAfter = $pos.nodeAfter
  const tail = nodeAfter?.isText ? nodeAfter.text!.match(/^\S*/)![0] : ''

  return {
    slashCommand,
    index,
    arg,
    range: { from: $pos.pos - query.length, to: $pos.pos + tail.length },
    query
  }
}

/**
 * Custom find() for @tiptap/suggestion: reports a match at the cursor while
 * it sits in the args region of an invoked command. Runs inside plugin
 * apply() (including for history remaps), so it must stay pure.
 */
export function findArgsMatch({ $position }: { $position: ResolvedPos }) {
  const context = resolveArgsContext($position)
  if (!context) return null
  return { range: context.range, query: context.query, text: context.query }
}
